import React from 'react';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Breadcrumbs } from '@mui/material';
import Header from '../PageHeader/page';

interface BreadcrumbItem {
  label: string;
  link: string;
}

interface PageHeroProps {
  title: string;
  subtitle?: string;
  description?: string;
  backgroundImage?: string;
  backgroundColor?: string;
  height?: string | number;
  showButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  contentAlign?: 'left' | 'center' | 'right';
  overlay?: boolean;
  overlayOpacity?: number;
  showBreadcrumbs?: boolean;
  breadcrumbs?: BreadcrumbItem[];
}

// Hero styled components

const HeroContainer = styled(Box)(({ theme }) => ({
  maxWidth: '1800px',
  margin: '0 auto',
  padding: '0 20px',
  maxHeight: '1000px',
  position: 'relative',
  overflow: 'hidden'
}));

const PageHeroContainer = styled(Box)<{ 
  backgroundImage?: string; 
  backgroundColor?: string;
  height?: string | number;
}>(({ theme, backgroundImage, backgroundColor, height }) => ({
  width: '100vw',
  height: '350px', // Fixed height - ignores the height prop to ensure consistency
  minHeight: '350px', // Ensure minimum height is maintained
  maxHeight: '350px', // Prevent growing beyond this height
  position: 'relative',
  left: '50%',
  right: '50%',
  marginLeft: '-50vw',
  marginRight: '-50vw',
  marginTop: '100px', // Add space for fixed header (TopBar + MainHeader)
  backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
  backgroundColor: backgroundColor || '#f5f5f5',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  // Responsive heights - consistent across all screen sizes
  [theme.breakpoints.down('xl')]: {
    marginTop: '104px',
    height: '350px',
    minHeight: '350px',
    maxHeight: '350px'
  },
  [theme.breakpoints.down('lg')]: {
    marginTop: '108px',
    height: '350px',
    minHeight: '350px',
    maxHeight: '350px'
  },
  [theme.breakpoints.down('md')]: {
    marginTop: '60px',
    height: '320px',
    minHeight: '320px',
    maxHeight: '320px'
  },
  [theme.breakpoints.down('sm')]: {
    height: '280px',
    minHeight: '280px',
    maxHeight: '280px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    marginTop: '116px',
    height: '350px',
    minHeight: '350px',
    maxHeight: '350px'
  }
}));

const Overlay = styled(Box)<{ opacity: number }>(({ opacity }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: `rgba(0, 0, 0, ${opacity})`,
  zIndex: 1,
}));

const ContentSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  zIndex: 2,
}));

const ContentWrapper = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  padding: theme.spacing(4),
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  fontSize: '3.5rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '2.5rem',
  },
}));

const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  alignItems: 'center',
  '& .MuiBreadcrumbs-separator': {
    color: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      marginLeft: '4px',
      marginRight: '4px',
    },
  },
  '& .MuiBreadcrumbs-ol': {
    flexWrap: 'nowrap',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  '& .MuiBreadcrumbs-li': {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.9rem',
    lineHeight: '1.2',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.85rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.8rem',
    },
  },
  '& .MuiTypography-root': {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: '120px',
    },
  },
  '& a': {
    color: 'rgba(255, 255, 255, 0.9)',
    textDecoration: 'none',
    transition: 'color 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: '120px',
    },
    '&:hover': {
      color: '#ffffff',
      textDecoration: 'underline',
    },
  },
}));

const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 300,
  marginBottom: theme.spacing(2),
  opacity: 0.9,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.2rem',
  },
}));

const HeroDescription = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  lineHeight: 1.6,
  marginBottom: theme.spacing(4),
  maxWidth: '800px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

const LetsTalkButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#85b47c',
  color: '#FFFFFF',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1.2rem',
  fontFamily: '"Lora", serif',
  padding: '12px 32px',
  borderRadius: '12px',
  border: `2px solid #85b47c`,
  position: 'relative',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  '& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiButton-label': {
    position: 'relative',
    zIndex: 2
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    backgroundColor: '#FFFFFF',
    transition: 'left 0.4s ease',
    zIndex: 1,
    borderRadius: '12px'
  },
  '&::after': {
    content: '"Próximos Cursos →"',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#85b47c',
    fontWeight: 600,
    fontSize: '1.2rem',
    fontFamily: '"Lora", serif',
    zIndex: 3,
    opacity: 0,
    transition: 'opacity 0.4s ease',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  '&:hover': {
    borderColor: '#85b47c',
    backgroundColor: '#85b47c',
    '&::before': {
      left: 0
    },
    '&::after': {
      opacity: 1
    },
    '& span': {
      opacity: 0
    }
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: '10px 24px'
  }
}));

const PageHero: React.FC<PageHeroProps> = ({
  title,
  subtitle,
  description,
  backgroundImage,
  backgroundColor,
  height,
  showButton = false,
  buttonText = 'Próximos Cursos →',
  buttonLink = '/cursos',
  contentAlign = 'center',
  overlay = true,
  overlayOpacity = 0.4,
  showBreadcrumbs = false,
  breadcrumbs = [],
}) => {
  const navigate = useNavigate();


  const getAlignment = () => {
    switch (contentAlign) {
      case 'left':
        return 'flex-start';
      case 'right':
        return 'flex-end';
      default:
        return 'center';
    }
  };

  const getTextAlign = () => {
    switch (contentAlign) {
      case 'left':
        return 'left';
      case 'right':
        return 'right';
      default:
        return 'center';
    }
  };

  return (
    <>
      {/* Use PageHeader component */}
      <Header />

      {/* Hero Content Section */}
      <HeroContainer>
        <PageHeroContainer
          backgroundImage={backgroundImage}
          backgroundColor={backgroundColor}
          height={height}
        >
          {overlay && backgroundImage && <Overlay opacity={overlayOpacity} />}
          <ContentSection>
            <ContentWrapper maxWidth="lg">
              <Stack
                spacing={2}
                alignItems={getAlignment()}
                sx={{
                  textAlign: getTextAlign(),
                  color: backgroundImage ? '#ffffff' : '#333333',
                }}
              >
                <HeroTitle
                  variant="h1"
                  sx={{
                    color: backgroundImage ? '#ffffff' : '#333333',
                    margin: contentAlign === 'center' ? '0 auto' : '0',
                    
                    marginBottom: 0,
                  }}
                >
                  {title}
                </HeroTitle>
                
                {subtitle && (
                  <HeroSubtitle
                    sx={{
                      color: backgroundImage ? '#ffffff' : '#666666',
                      margin: contentAlign === 'center' ? '0 auto' : '0',
                      marginTop: '-8px',
                    }}
                  >
                    {subtitle}
                  </HeroSubtitle>
                )}
                
                {description && (
                  <HeroDescription
                    sx={{
                      color: backgroundImage ? '#ffffff' : '#666666',
                      margin: contentAlign === 'center' ? '0 auto' : '0',
                    }}
                  >
                    {description}
                  </HeroDescription>
                )}
                
                {showButton && (
                  <LetsTalkButton
                    onClick={() => navigate(buttonLink)}
                    sx={{
                      alignSelf: contentAlign === 'center' ? 'center' : 
                                contentAlign === 'left' ? 'flex-start' : 'flex-end',
                    }}
                  >
                    {buttonText}
                  </LetsTalkButton>
                )}
              </Stack>
            </ContentWrapper>
          </ContentSection>
          
          {showBreadcrumbs && breadcrumbs.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: 12, sm: 16 },
                left: 0,
                right: 0,
                zIndex: 2,
                px: { xs: 2, sm: 0 },
              }}
            >
              <Container maxWidth="lg">
                <StyledBreadcrumbs
                  separator={<NavigateNextIcon fontSize="small" />}
                  aria-label="breadcrumb"
                  sx={{
                    justifyContent: 'center',
                    display: 'flex',
                    maxWidth: '100%',
                    '& .MuiBreadcrumbs-ol': {
                      maxWidth: '100%',
                      paddingLeft: { xs: 1, sm: 0 },
                      paddingRight: { xs: 1, sm: 0 },
                    },
                  }}
                >
                  {breadcrumbs.map((crumb, index) => (
                    index === breadcrumbs.length - 1 ? (
                      <Typography 
                        key={index} 
                        component="span"
                        sx={{ 
                          color: 'rgba(255, 255, 255, 1)', 
                          fontWeight: 500,
                          fontSize: 'inherit',
                          lineHeight: 'inherit',
                          display: 'inline-block',
                          maxWidth: { xs: '150px', sm: 'none' },
                          overflow: { xs: 'hidden', sm: 'visible' },
                          textOverflow: { xs: 'ellipsis', sm: 'clip' },
                          whiteSpace: { xs: 'nowrap', sm: 'normal' },
                        }}
                        title={crumb.label}
                      >
                        {crumb.label}
                      </Typography>
                    ) : (
                      <Link
                        key={index}
                        to={crumb.link}
                        style={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          textDecoration: 'none',
                          display: 'inline-block',
                        }}
                        title={crumb.label}
                      >
                        {crumb.label}
                      </Link>
                    )
                  ))}
                </StyledBreadcrumbs>
              </Container>
            </Box>
          )}
        </PageHeroContainer>
      </HeroContainer>
    </>
  );
};

export default PageHero;